import { z } from "zod";

/** Book states on the wire (camelCase). The DB enum uses snake_case; src/db/schema/enums.ts maps them. */
export const BOOK_STATES = [
  "uploaded",
  "parsed",
  "confirmed",
  "styleSheetReady",
  "calibrating",
  "refining",
  "inReview",
  "approved",
  "rendering",
  "rendered",
  "failed",
] as const;
export const BookStateSchema = z.enum(BOOK_STATES);
export type BookState = z.infer<typeof BookStateSchema>;

export const CHAPTER_STATES = [
  "pending",
  "refining",
  "inReview",
  "approved",
  "rendering",
  "rendered",
  "excluded",
  "failed",
] as const;
export const ChapterStateSchema = z.enum(CHAPTER_STATES);
export type ChapterState = z.infer<typeof ChapterStateSchema>;

export const JOB_KINDS = [
  "metadata",
  "parse",
  "chapterProposal",
  "styleSheet",
  "refine",
  "render",
  "assemble",
  "sample",
  "voicesSync",
  "quality",
] as const;
export const JobKindSchema = z.enum(JOB_KINDS);
export type JobKind = z.infer<typeof JobKindSchema>;

export const JOB_STATUSES = ["queued", "running", "succeeded", "failed", "cancelled"] as const;
export const JobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatusSchema>;

export const BOOK_LANGUAGES = ["de", "en"] as const;
export const BookLanguageSchema = z.enum(BOOK_LANGUAGES);
export type BookLanguage = z.infer<typeof BookLanguageSchema>;

export const TTS_MODELS = ["eleven_v3", "eleven_multilingual_v2"] as const;
export const TtsModelSchema = z.enum(TTS_MODELS);
export type TtsModel = z.infer<typeof TtsModelSchema>;

export const PROVIDERS = ["openrouter", "elevenlabs"] as const;
export const ProviderSchema = z.enum(PROVIDERS);
export type Provider = z.infer<typeof ProviderSchema>;

export const SOURCE_FORMATS = ["docx", "epub", "markdown"] as const;
export const SourceFormatSchema = z.enum(SOURCE_FORMATS);
export type SourceFormat = z.infer<typeof SourceFormatSchema>;

/**
 * eleven_v3 stability on the wire. The DB stores the ElevenLabs number in
 * voice_settings; STABILITY_VALUES is the one mapping (plan: 0.0/0.5/1.0).
 */
export const STABILITIES = ["creative", "natural", "robust"] as const;
export const StabilitySchema = z.enum(STABILITIES);
export type Stability = z.infer<typeof StabilitySchema>;
export const STABILITY_VALUES: Readonly<Record<Stability, number>> = Object.freeze({
  creative: 0.0,
  natural: 0.5,
  robust: 1.0,
});
export const DEFAULT_STABILITY: Stability = "natural";
export const DEFAULT_TTS_MODEL: TtsModel = "eleven_v3";

export const VOICE_SOURCES = ["sharedLibrary", "professionalClone"] as const;
export const VoiceSourceSchema = z.enum(VOICE_SOURCES);
export type VoiceSource = z.infer<typeof VoiceSourceSchema>;

/** The one Voice Role of version one; dialogue roles join this list later without a script format change. */
export const VOICE_ROLES = ["narrator"] as const;
export const VoiceRoleSchema = z.enum(VOICE_ROLES);
export type VoiceRole = z.infer<typeof VoiceRoleSchema>;

export const SAMPLE_KINDS = ["voiceSample", "pronunciationCheck"] as const;
export const SampleKindSchema = z.enum(SAMPLE_KINDS);
export type SampleKind = z.infer<typeof SampleKindSchema>;

export const SCRIPT_SOURCES = ["refinement", "edit", "restore"] as const;
export const ScriptSourceSchema = z.enum(SCRIPT_SOURCES);
export type ScriptSource = z.infer<typeof ScriptSourceSchema>;

export const STYLE_SHEET_ORIGINS = ["llm", "authorEdit", "instruction"] as const;
export const StyleSheetOriginSchema = z.enum(STYLE_SHEET_ORIGINS);
export type StyleSheetOrigin = z.infer<typeof StyleSheetOriginSchema>;

export const NODE_TYPES = [
  "heading",
  "paragraph",
  "listItem",
  "table",
  "footnote",
  "figure",
  "caption",
  "pageBreak",
] as const;
export const NodeTypeSchema = z.enum(NODE_TYPES);
export type NodeType = z.infer<typeof NodeTypeSchema>;
